import React, { useState, useCallback } from 'react';
import Button from 'components/UI/V3/Button/Button';
import { ServiceNameSection } from './Sections/ServiceName/ServiceNameSection';
import { DescriptionSection } from './Sections/Description/DescriptionSection';
import { DurationSection } from './Sections/Duration/DurationSection';
import { BookingTypeSection } from './Sections/BookingType/BookingTypeSection';
import { MaxPetsSection } from './Sections/MaxPets/MaxPetsSection';
import { InHomeServiceSection } from './Sections/InHomeService/InHomeServiceSection';
import { OnlineBookingSection } from './Sections/OnlineBooking/OnlineBookingSection';
import { ColorCodeSection } from './Sections/ColorCode/ColorCodeSection';
import { ServiceFormsSection } from 'components/Service/Forms/ServiceFormsSection';
import { AssociatedForm } from 'model/Forms';
import { SESSION_BOOKING_TYPES } from 'model/Service';
import styles from './TrainingDetailsStep.module.scss';

interface TrainingDetailsFormData {
    name: string;
    description?: string;
    durationHours: number;
    durationMinutes: number;
    bookingType: string;
    maxPets: number;
    inHomeService: boolean;
    olbEnabled: boolean;
    color?: string;
    forms?: AssociatedForm[];
}

interface TrainingDetailsStepProps {
    formData: TrainingDetailsFormData;
    serviceId?: number;
    onNext: (data: TrainingDetailsFormData) => void;
}

export const TrainingDetailsStep: React.FC<TrainingDetailsStepProps> = ({ formData, serviceId, onNext }) => {
    const [name, setName] = useState(formData.name || '');
    const [description, setDescription] = useState(formData.description || '');
    const [durationHours, setDurationHours] = useState(formData.durationHours || 0);
    const [durationMinutes, setDurationMinutes] = useState(formData.durationMinutes || 0);
    const [bookingType, setBookingType] = useState(formData.bookingType || SESSION_BOOKING_TYPES.PRIVATE_SESSION);
    const [maxPets, setMaxPets] = useState(formData.maxPets || 2);
    const [inHomeService, setInHomeService] = useState(formData.inHomeService || false);
    const [olbEnabled, setOlbEnabled] = useState(formData.olbEnabled ?? true);
    const [forms, setForms] = useState<AssociatedForm[]>(formData.forms || []);
    const [color, setColor] = useState<string | null>(formData.color || null);
    const [nameError, setNameError] = useState<string>('');
    const [durationError, setDurationError] = useState<string>('');

    const validateName = (value: string): boolean => {
        if (!value || value.trim() === '') {
            setNameError('Service name is required');
            return false;
        }
        setNameError('');
        return true;
    };

    const validateDuration = (hours: number, minutes: number): boolean => {
        if (hours === 0 && minutes === 0) {
            setDurationError('Duration must be at least 1 minute');
            return false;
        }
        setDurationError('');
        return true;
    };

    const handleNext = () => {
        const isNameValid = validateName(name);
        const isDurationValid = validateDuration(durationHours, durationMinutes);

        if (!isNameValid || !isDurationValid) {
            return;
        }

        onNext({
            name: name.trim(),
            description: description?.trim() || '',
            durationHours,
            durationMinutes,
            bookingType,
            maxPets,
            inHomeService,
            olbEnabled,
            color: color || undefined,
            forms
        });
    };

    return (
        <div className={styles.container}>
            <div className={styles.sectionsContainer}>
                <ServiceNameSection
                    value={name}
                    error={nameError}
                    onChange={e => {
                        setName(e.target.value);
                        if (nameError) validateName(e.target.value);
                    }}
                />

                <DescriptionSection
                    value={description}
                    error=""
                    onChange={e => setDescription(e.target.value)}
                />

                <DurationSection
                    hours={durationHours}
                    minutes={durationMinutes}
                    onHoursChange={h => {
                        setDurationHours(h);
                        if (durationError) validateDuration(h, durationMinutes);
                    }}
                    onMinutesChange={m => {
                        setDurationMinutes(m);
                        if (durationError) validateDuration(durationHours, m);
                    }}
                    error={durationError}
                />

                <BookingTypeSection
                    bookingType={bookingType}
                    onChange={value => {
                        setBookingType(value);
                        if (value === 'group_session' && maxPets < 2) {
                            setMaxPets(2);
                        }
                    }}
                />

                <MaxPetsSection
                    maxPets={maxPets}
                    bookingType={bookingType}
                    onChange={setMaxPets}
                />

                <InHomeServiceSection
                    inHomeService={inHomeService}
                    onChange={setInHomeService}
                />

                <OnlineBookingSection
                    checked={olbEnabled}
                    onChange={setOlbEnabled}
                />
                <ServiceFormsSection
                    forms={forms}
                    serviceId={serviceId}
                    serviceCategory="training"
                    onChange={setForms}
                />

                <ColorCodeSection
                    color={color}
                    onChange={setColor}
                    className={styles.colorCodeSection}
                />
            </div>

            <div className={styles.actions}>
                <Button
                    label="Next"
                    onClick={handleNext}
                    disabled={!name || !!nameError || !!durationError}
                    size="large"
                    variant="primary"
                />
            </div>
        </div>
    );
};
